import { v4 as uuid } from "uuid";
import { List, Item } from "linked-list";

// Set to true to enable verbose MWE debugging
const MWE_DEBUG = false;

export class Word extends Item {
  constructor(token) {
    super();
    this.id = uuid();
    let bookmark = token.bookmark;
    this.word = token.text;
    this.translation = null;
    this.total_tokens = 1;
    if (bookmark) {
      if (bookmark.is_mwe) {
        this.translation = bookmark.translation;
        this.bookmark_id = bookmark.id;
        this.mweExpression = bookmark.origin;
        // For contiguous MWEs with merged tokens, show combined text
        if (token.mergedTokens && token.mergedTokens.length > 1) {
          // Join respecting has_space property (like hyphenated words)
          this.word = token.mergedTokens.reduce((acc, t, i) => {
            if (i === 0) return t.text.trim();
            const prev = token.mergedTokens[i - 1];
            const separator = prev.has_space === false ? "" : " ";
            return acc + separator + t.text.trim();
          }, "");
          this.total_tokens = token.mergedTokens.length;
        } else {
          // Separated MWE - keep original token text
          this.total_tokens = 1;
        }
      } else {
        // For single-word bookmarks, keep original token text (preserves case)
        // For multi-word, reconstruct from merged tokens to preserve original case
        if (bookmark.t_total_token === 1) {
          // Single word: keep token.text (e.g., "Dingue" not "dingue")
          // this.word is already set to token.text above
        } else if (token.mergedTokens && token.mergedTokens.length > 1) {
          // Multi-word: reconstruct from merged tokens to preserve original case
          this.word = token.mergedTokens.map((t) => t.text).join(" ");
        } else {
          // Fallback to bookmark.origin if no merged tokens available
          this.word = bookmark.origin;
        }
        this.translation = bookmark.translation;
        this.total_tokens = bookmark.t_total_token;
        this.bookmark_id = bookmark.id;
      }
    }
    this.token = token;
    if (token.mergedTokens) this.mergedTokens = [...token.mergedTokens];
    else this.mergedTokens = [{ ...token }];
    // For separated MWE partners: token has mweExpression but no bookmark
    // Transfer it to the Word so it gets MWE styling
    if (token.mweExpression && !this.mweExpression) {
      this.mweExpression = token.mweExpression;
    }
    // Transfer isMwePartner flag for separated MWE partners
    if (token.isMwePartner) {
      this.isMwePartner = true;
    }
  }

  updateTranslation(translation, service_name, bookmark_id, competing_translations = null, disagreement = false, alternatives = null) {
    this.translation = translation;
    this.service_name = service_name;
    this.bookmark_id = bookmark_id;
    this.competing_translations = competing_translations;
    this.disagreement = disagreement;
    // ADR 022: full deduped, vote-ordered provider list from the backend,
    // including the winner at index 0. AlterMenu renders directly from this
    // — no second round of provider calls needed for the stable menu rows.
    //
    // Always overwrite (don't gate on truthy) so a re-translation with no
    // alternatives doesn't leave a stale list next to fresh competing_/
    // disagreement values. The lazy-fetch path on the own-past-translation
    // case populates `this.alternatives` directly via fetchAlternatives,
    // not through updateTranslation, so clearing here is safe.
    this.alternatives = alternatives;
  }

  splitIntoComponents() {
    // mergedTokens contains all the tokens in order that were merged
    // in the current word.
    // Used when deleting translations
    let wordList = this.mergedTokens.map((each) => new Word(each));
    this.append(wordList[0]);

    for (let i = 0; i < wordList.length - 1; i++) {
      wordList[i].append(wordList[i + 1]);
    }

    this.detach();
  }

  /**
   * Split this word back into components and clear all MWE metadata.
   * Used when user explicitly ungroups an MWE via "Ungroup words".
   * Clears mwe_group_id so the words behave like normal non-MWE words.
   * Also clears MWE data from any partner words in the same sentence (for separated MWEs).
   */
  splitAndClearMWE() {
    // For separated MWEs, find and clear MWE data from partner words first
    // (before we lose track of them by clearing our own mwe_group_id)
    if (this.token.mwe_group_id) {
      const partners = this.findMWEPartners();
      for (const partner of partners) {
        if (partner !== this) {
          // Clear MWE metadata from partner's token
          delete partner.token.mwe_group_id;
          delete partner.token.mwe_role;
          delete partner.token.mwe_is_separated;
          delete partner.token.mwe_partner_indices;
          delete partner.mweExpression;
          delete partner.isMwePartner;
        }
      }
    }

    // Clear MWE metadata from all merged tokens before creating new words
    const clearedTokens = this.mergedTokens.map((token) => {
      const cleared = { ...token };
      delete cleared.mwe_group_id;
      delete cleared.mwe_role;
      delete cleared.mwe_is_separated;
      delete cleared.mwe_partner_indices;
      delete cleared.mweExpression;
      delete cleared.isMwePartner;
      return cleared;
    });

    let wordList = clearedTokens.map((each) => new Word(each));
    this.append(wordList[0]);

    for (let i = 0; i < wordList.length - 1; i++) {
      wordList[i].append(wordList[i + 1]);
    }

    this.detach();
    return wordList;
  }

  /**
   * The joins in this word that a learner made, as indices into
   * `mergedTokens`: a seam at `i` sits between token `i - 1` and token `i`.
   *
   * Merged tokens keep the `mwe_group_id` they came with, so the detector's
   * own joins are exactly those between two tokens of the same group. Every
   * other join was a fusion: two groups ("har fundet" + "et stykke"), a group
   * and a plain word, or two plain words. The last case has no group id on
   * either side, which is why a missing id counts as a seam rather than as a
   * match -- otherwise hand-fused plain words could never be taken apart.
   *
   * Derived rather than recorded, so it survives a reload: restored bookmarks
   * rebuild `mergedTokens` from the tokenizer's tokens, ids included.
   */
  seams() {
    const out = [];
    for (let i = 1; i < this.mergedTokens.length; i++) {
      const a = this.mergedTokens[i - 1].mwe_group_id;
      const b = this.mergedTokens[i].mwe_group_id;
      if (!a || a !== b) out.push(i);
    }
    return out;
  }

  /**
   * The text between seams, for rendering a marker at each join.
   * `seam` is the index `breakAtSeam` takes to split just before the piece;
   * the first piece has none.
   */
  pieces() {
    const bounds = [0, ...this.seams(), this.mergedTokens.length];
    return bounds.slice(0, -1).map((start, k) => ({
      text: joinTokenTexts(this.mergedTokens.slice(start, bounds[k + 1])),
      seam: k === 0 ? null : start,
    }));
  }

  /**
   * Undo one learner join, leaving every other grouping as it was.
   *
   * This is not `splitAndClearMWE`: that says "the detector was wrong" and
   * flattens to loose words. Here the learner is changing their mind about
   * their own fusion, so a piece that is a whole detector group comes back as
   * that expression, and a piece that still holds other seams stays fused.
   *
   * Returns the two new words, already linked in place of this one.
   */
  breakAtSeam(i) {
    const [left, right] = [this.mergedTokens.slice(0, i), this.mergedTokens.slice(i)].map(wordFromTokens);
    this.append(left);
    left.append(right);
    this.detach();
    return [left, right];
  }

  fuseWithPrevious(api) {
    this.word = this.prev.word + " " + this.word;
    if (this.prev && this.prev.bookmark_id) {
      // consider hide bookmark; here
      // this would allow caching of partial translations
      // would also keep a better trace of user interactions?
      // would it? i know they clicked on every word already...
      // it won't bring anything... besides the partial translation again..
      // which might still be useful for a teacher helping students...?
      // To think more about
      api.deleteBookmark(this.prev.bookmark_id);
    }
    // Keep every token from both sides, in reading order. This used to push
    // only `this.token`, which silently dropped the rest of a multi-token
    // word: fusing "et stykke" onto "har fundet" kept "et" and lost "stykke".
    // Splitting rebuilds the text from this list, so a token missing here
    // disappears from the page. fuseWithNext has always taken the whole list;
    // this direction now matches.
    this.mergedTokens = [...this.prev.mergedTokens, ...this.mergedTokens];

    this.token = this.prev.token;
    this.total_tokens += this.prev.total_tokens;

    this.prev.detach();
    this.dropMWEIdentity();
    return this;
  }

  fuseWithNext(api) {
    // Next is in relation to the word you click. (Next has translation)
    this.word = this.word + " " + this.next.word;
    if (this.next && this.next.bookmark_id) {
      api.deleteBookmark(this.next.bookmark_id);
    }
    this.total_tokens += this.next.total_tokens;
    this.mergedTokens = [...this.mergedTokens, ...this.next.mergedTokens];
    this.next.detach();
    this.dropMWEIdentity();
    return this;
  }

  /**
   * Resolve which word should actually be translated when this one is tapped,
   * performing whatever fusion that implies.
   *
   * Tapping any member of an MWE translates the expression, and a contiguous
   * expression can still take an already-translated neighbour, so one tap can
   * both collapse a group and widen it. Everything else fuses with translated
   * neighbours only.
   *
   * Returns the word to translate, or null when a partner already carries the
   * translation and a second one would duplicate it.
   *
   * Lives here rather than in the readers because InteractiveText and
   * PublicInteractiveText both need it and had drifted apart: the public
   * reader could fuse onto an MWE but an MWE could not take a neighbour.
   *
   * `onMWEFusion` fires only when partners were collapsed, which is when the
   * caller needs to re-render to show the fused word.
   */
  fuseForTranslation(api, fuseWithNeighbours, onMWEFusion = null) {
    if (this.isMWE()) {
      const fused = this.fuseMWEPartners(api);
      if (fused === null) return null;
      const result = fuseWithNeighbours ? fused.fuseWithNeighborsIfNeeded(api) : fused;
      if (onMWEFusion) onMWEFusion();
      return result;
    }
    return fuseWithNeighbours ? this.fuseWithNeighborsIfNeeded(api) : this;
  }

  /**
   * After a fusion has widened this word past the detector's grouping, it is
   * no longer that grouping -- it is a span the learner built.
   *
   * This matters beyond tidiness. fuseMWEPartners sets `mweExpression`, and
   * InteractiveText translates `word.mweExpression || word.word`, so a stale
   * `mweExpression` means the learner sees "at finde ud af" fused and gets a
   * translation of "finde ud af". The fused text lives in `this.word`, so the
   * expression has to go with the grouping.
   *
   * The token is copied before its MWE fields are removed: it is shared with
   * the paragraph structure, and deleting in place would strip the styling
   * from tokens that are still rendered.
   */
  dropMWEIdentity() {
    if (!this.mweExpression && !this.token?.mwe_group_id) return;

    delete this.mweExpression;
    delete this.isMwePartner;

    if (this.token?.mwe_group_id) {
      this.token = { ...this.token };
      delete this.token.mwe_group_id;
      delete this.token.mwe_role;
      delete this.token.mwe_is_separated;
      delete this.token.mwe_partner_indices;
    }
  }

  fuseWithNeighborsIfNeeded(api) {
    // api: this is needed because we want to delete the previous
    // bookmark in the case of a fusion; no need to save the partial
    // translations to the DB; we used to do that; but it was just
    // polluting the DB
    let newWord = this;

    // A separated MWE cannot be widened from either end: "extend" is not a
    // coherent operation on a discontinuous span, and the backend saves no
    // bookmark for one (zeeguu/api#769), so the result would have nowhere to
    // persist. Enforced here rather than at the call sites, because
    // PublicInteractiveText reaches this method by its own path.
    if (this.token?.mwe_group_id && this.token?.mwe_is_separated) {
      return newWord;
    }

    // A contiguous MWE can be extended: fusing onto it widens the unit, which
    // is how a learner repairs a grouping the detector made too narrow. The
    // detector deliberately errs narrow because this is cheap and in-flow --
    // learners fused by hand 13,725 times in 2026 and ungrouped only 130.
    //
    // A SEPARATED MWE stays closed. "Extend" is not a coherent operation on a
    // discontinuous span -- adding a token inside the gap ("rufe dich an" ->
    // "rufe dich an" + what?) is not a unit -- and the backend refuses to save
    // a bookmark for a separated MWE at all, because (token_i, total_tokens)
    // cannot express a gap. Fusing onto one would produce a word with nowhere
    // to persist. See zeeguu/api#769.
    const isClosedMWE = (neighbour) =>
      !!neighbour?.token?.mwe_group_id && !!neighbour?.token?.mwe_is_separated;

    if (this.prev && this.prev.translation && !isClosedMWE(this.prev)) {
      newWord = this.fuseWithPrevious(api);
    }

    if (this.next && this.next.translation && !isClosedMWE(this.next)) {
      newWord = this.fuseWithNext(api);
    }
    return newWord;
  }

  /**
   * Check if this word is part of a Multi-Word Expression (MWE).
   * MWE metadata comes from the backend MWE detector.
   *
   * LIMITATION: MWE groupings are determined by the backend during tokenization.
   * If the backend incorrectly identifies an MWE (e.g., "sollte belastet" instead
   * of "sollte belastet werden"), the user cannot currently correct this on the
   * frontend. They can only:
   * 1. Delete the incorrect MWE translation
   * 2. Report the issue via "Report Issue" button
   * 3. Translate words individually
   *
   * Future improvement: Add an "Edit MWE" dialog that allows users to:
   * - Break/ungroup incorrectly detected MWEs
   * - Extend MWEs to include additional words
   * - Store corrections and potentially send them back to improve the detector
   */
  isMWE() {
    return !!this.token.mwe_group_id;
  }

  /**
   * Get the MWE group ID for this word.
   */
  getMWEGroupId() {
    return this.token.mwe_group_id;
  }

  /**
   * Find all MWE partner Word objects in the linked list.
   * Returns array of Word objects that share the same mwe_group_id.
   */
  findMWEPartners() {
    if (!this.isMWE()) return [this];

    const groupId = this.getMWEGroupId();
    const sentenceIndex = this.token.sent_i;
    const partners = [this];

    // Search backwards in linked list for partners in same sentence
    let current = this.prev;
    while (current && current.token.sent_i === sentenceIndex) {
      if (current.token.mwe_group_id === groupId) {
        partners.unshift(current);
      }
      current = current.prev;
    }

    // Search forwards in linked list for partners in same sentence
    current = this.next;
    while (current && current.token.sent_i === sentenceIndex) {
      if (current.token.mwe_group_id === groupId) {
        partners.push(current);
      }
      current = current.next;
    }

    return partners;
  }

  /**
   * Get the MWE expression text by joining all partner words.
   * Used for checking against disabled MWE expressions.
   */
  getMWEExpression() {
    if (!this.isMWE()) return null;
    const partners = this.findMWEPartners();
    return partners.map(p => p.word.trim()).join(" ").toLowerCase();
  }

  /**
   * Get the text of the sentence containing this word.
   * Used for computing sentence hash for MWE override storage.
   */
  getSentenceText() {
    const sentenceIndex = this.token.sent_i;
    const words = [];

    // Collect all words in this sentence by traversing backwards then forwards
    let current = this;
    while (current.prev && current.prev.token.sent_i === sentenceIndex) {
      current = current.prev;
    }

    // Now traverse forwards collecting all words in this sentence
    while (current && current.token.sent_i === sentenceIndex) {
      words.push(current.word);
      current = current.next;
    }

    return words.join(" ");
  }

  /**
   * Prepare MWE for translation.
   * - Adjacent MWE words: fuse visually into single word with combined translation
   * - Non-adjacent MWE words (gaps in between): keep separate, use combined expression for translation only
   */
  fuseMWEPartners(api) {
    const partners = this.findMWEPartners();
    if (partners.length <= 1) return this;

    // Helper to join words without space after hyphen (e.g., "și-" + "a" → "și-a")
    const joinWords = (words) => {
      return words.reduce((acc, word, i) => {
        if (i === 0) return word;
        // Don't add space if previous word ends with hyphen
        const prevWord = words[i - 1];
        if (prevWord.endsWith("-")) return acc + word;
        return acc + " " + word;
      }, "");
    };

    // Build combined expression text (for translation)
    const combinedExpression = joinWords(partners.map((p) => p.word));

    // Check if all partners are adjacent (no gaps > 1)
    let allAdjacent = true;
    for (let i = 1; i < partners.length; i++) {
      const gap = partners[i].token.token_i - partners[i - 1].token.token_i;
      if (gap > 1) {
        allAdjacent = false;
        break;
      }
    }

    // If first partner already has translation, don't create another
    const firstPartner = partners[0];
    if (firstPartner.translation) {
      MWE_DEBUG && console.log("MWE already translated, skipping:", combinedExpression);
      return null; // Signal to not translate
    }

    // Find groups of TRULY ADJACENT partners to fuse
    // Only fuse words that are directly next to each other (gap == 1)
    // Words with anything between them should NOT be fused visually
    let fusionGroups = [];
    let currentGroup = [partners[0]];

    for (let i = 1; i < partners.length; i++) {
      const gap = partners[i].token.token_i - partners[i - 1].token.token_i;
      if (gap === 1) {
        // Truly adjacent - fuse with current group
        currentGroup.push(partners[i]);
      } else {
        // Not adjacent (has words between) - start a new group
        fusionGroups.push(currentGroup);
        currentGroup = [partners[i]];
      }
    }
    fusionGroups.push(currentGroup);

    // Only fuse the first group (which contains the head word)
    // Other groups stay as separate words with MWE styling
    const mainGroup = fusionGroups[0];
    const mainExpression = joinWords(mainGroup.map((p) => p.word));

    MWE_DEBUG &&
      console.log("MWE fusion:", {
        fullExpression: combinedExpression,
        fusedExpression: mainExpression,
        numGroups: fusionGroups.length,
        separated: this.token?.mwe_is_separated,
      });

    // Always use first word as the visual head, regardless of mwe_role
    // mwe_role indicates the linguistic/semantic head from the backend,
    // but for UI we want the first word to remain visible and show combined text
    let headWord = mainGroup[0];

    // Update head word with fused group info
    let mergedTokens = mainGroup.map((p) => ({ ...p.token }));
    headWord.word = mainExpression;
    headWord.mweExpression = combinedExpression; // Keep full expression for translation
    headWord.mergedTokens = mergedTokens;
    headWord.total_tokens = mainGroup.length;

    // Remove other partners in the main group from the list
    for (const partner of mainGroup) {
      if (partner !== headWord) {
        partner.token.skipRender = true;
        partner.detach();
      }
    }

    // Partners in other groups keep their styling but stay in place
    // (they'll show MWE color but won't be fused)

    return headWord;
  }

  // Prevent circular reference issues during JSON serialization (e.g., Sentry Replay)
  // The Word class extends Item from linked-list, which has prev/next pointers
  // that create circular references and cause "Maximum call stack size exceeded" errors
  toJSON() {
    return {
      id: this.id,
      word: this.word,
      translation: this.translation,
      total_tokens: this.total_tokens,
      bookmark_id: this.bookmark_id,
      service_name: this.service_name,
      // Omit prev/next (inherited from Item) to break circular reference
      // Omit token and mergedTokens as they may contain complex nested objects
    };
  }
}

export default class LinkedWordList {
  constructor(sentList) {
    let result = splitTextIntoWords(sentList);
    this.linkedWords = List.from(result);
  }

  getWords() {
    return this.linkedWords.toArray();
  }
}

// Private functions

// Join token texts the way the Word constructor does, so "și-" + "a" stays
// "și-a" wherever the tokenizer says there was no space.
function joinTokenTexts(tokens) {
  return tokens.reduce((acc, t, i) => {
    if (i === 0) return t.text.trim();
    const separator = tokens[i - 1].has_space === false ? "" : " ";
    return acc + separator + t.text.trim();
  }, "");
}

// Rebuild an untranslated Word from a run of merged tokens, as breakAtSeam
// needs for each side.
function wordFromTokens(tokens) {
  const clean = tokens.map((t) => {
    const c = { ...t };
    // Render and restoration state belongs to the word the tokens came from.
    delete c.bookmark;
    delete c.mergedTokens;
    delete c.skipRender;
    delete c.mweExpression;
    delete c.isMwePartner;
    return c;
  });

  const piece = new Word({ ...clean[0] });
  piece.mergedTokens = clean;
  piece.total_tokens = clean.length;
  piece.word = joinTokenTexts(clean);

  if (piece.seams().length > 0) {
    // Still a learner's span, so it is not the detector's expression either.
    piece.dropMWEIdentity();
  } else if (clean.length > 1 && clean[0].mwe_group_id) {
    // Exactly one detector group: give it back its expression, so it is
    // styled and translated as the MWE it was before the fusion.
    piece.mweExpression = piece.word;
  }
  return piece;
}

function splitTextIntoWords(sentList) {
  let wordList = [];
  for (let sent_i = 0; sent_i < sentList.length; sent_i++) {
    let sent = sentList[sent_i];
    for (let token_i = 0; token_i < sent.length; token_i++) {
      let token = sent[token_i];
      if (token.skipRender) continue;
      wordList.push(new Word(token));
    }
  }
  return wordList;
}
